'use client';

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Advanced Crop & Compress Utility
 * Handles coordinate scaling, rotation, flipping, and enforces a MAX_DIMENSION (1200px) with WebP compression.
 */
export async function getCroppedImg(
  imageOrSrc: string | HTMLImageElement,
  pixelCrop: { x: number; y: number; width: number; height: number },
  rotation = 0,
  flip = { horizontal: false, vertical: false }
): Promise<Blob | null> {
  const image = typeof imageOrSrc === 'string' ? await createImage(imageOrSrc) : imageOrSrc;

  // 1. Calculate the scale factor between displayed size and natural size
  // react-image-crop's PixelCrop is based on the image's displayed dimensions (image.width / image.height)
  // We must map these to the image's natural dimensions (image.naturalWidth / image.naturalHeight)
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Scale coordinates to natural pixel space
  const naturalCrop = {
    x: pixelCrop.x * scaleX,
    y: pixelCrop.y * scaleY,
    width: pixelCrop.width * scaleX,
    height: pixelCrop.height * scaleY,
  };

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // 2. Calculate bounding box for rotation support
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.naturalWidth,
    image.naturalHeight,
    rotation
  );

  // Set initial canvas size to the rotated bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // 3. Apply transformations (Rotation & Flip)
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.naturalWidth / 2, -image.naturalHeight / 2);

  // 4. Draw the original image onto the transformed canvas
  ctx.drawImage(image, 0, 0);

  // 5. Extract the cropped area from the transformed canvas
  // We use a secondary canvas to avoid pixel data overhead
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = naturalCrop.width;
  cropCanvas.height = naturalCrop.height;
  const cropCtx = cropCanvas.getContext('2d');

  if (!cropCtx) return null;

  cropCtx.drawImage(
    canvas,
    naturalCrop.x,
    naturalCrop.y,
    naturalCrop.width,
    naturalCrop.height,
    0,
    0,
    naturalCrop.width,
    naturalCrop.height
  );

  // 6. --- COMPRESSION & SCALING ---
  // If the natural crop is massive, we downscale it to max 1200px
  const MAX_DIMENSION = 1200;
  const resizeRatio = Math.min(
    1,
    MAX_DIMENSION / naturalCrop.width,
    MAX_DIMENSION / naturalCrop.height
  );

  const finalWidth = Math.round(naturalCrop.width * resizeRatio);
  const finalHeight = Math.round(naturalCrop.height * resizeRatio);

  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = finalWidth;
  finalCanvas.height = finalHeight;
  const finalCtx = finalCanvas.getContext('2d');

  if (finalCtx) {
    finalCtx.imageSmoothingEnabled = true;
    finalCtx.imageSmoothingQuality = 'high';

    finalCtx.drawImage(
      cropCanvas,
      0,
      0,
      naturalCrop.width,
      naturalCrop.height,
      0,
      0,
      finalWidth,
      finalHeight
    );
  }

  // 7. Export as WebP (aggressive compression + alpha preservation)
  return new Promise((resolve) => {
    (finalCtx ? finalCanvas : cropCanvas).toBlob(
      (file) => {
        resolve(file);
      },
      'image/webp',
      0.85
    );
  });
}
